/**
 * 大会管理
 *
 * 大会の作成、試合結果の記録、次の試合の取得、ブラケット進行を管理する。
 */
import {
  TournamentState,
  TournamentMode,
  TournamentPhase,
  TournamentMatch,
  TournamentMatchResult,
} from '../types/Tournament';
import { AILevel, AIConfig } from '../ai/types';
import { createParticipants } from './participantFactory';
import { generateRoundRobinMatches } from './roundRobinScheduler';
import { generateEliminationBracket } from './eliminationBracketGenerator';
import { loadGameProgress } from '../utils/gameProgressManager';

/**
 * 新しい大会を作成する。
 *
 * @param mode 大会種類
 * @param playerMonsterId プレイヤーが選択したモンスターID
 * @param aiLevel CPU共通AIレベル
 * @returns 大会の初期状態
 */
export function createTournament(
  mode: TournamentMode,
  playerMonsterId: string,
  aiLevel: AILevel,
  playerAIConfig?: AIConfig,
): TournamentState {
  const { unlockedHiddenMonsters } = loadGameProgress();
  const participants = createParticipants(playerMonsterId, playerAIConfig, unlockedHiddenMonsters);
  const participantIds = participants.map((p) => p.id);

  const matches = mode === TournamentMode.ROUND_ROBIN
    ? generateRoundRobinMatches(participantIds)
    : generateEliminationBracket(participantIds);

  return {
    mode,
    participants,
    matches,
    currentMatchIndex: 0,
    phase: TournamentPhase.NOT_STARTED,
    aiLevel,
  };
}

/**
 * 試合結果を記録し、参加者の成績を更新する。
 * 既に結果が記録済みの場合は上書きしない。
 *
 * @param state 現在の大会状態
 * @param matchIndex 試合インデックス
 * @param result 試合結果
 * @returns 更新後の大会状態
 */
export function recordMatchResult(
  state: TournamentState,
  matchIndex: number,
  result: TournamentMatchResult,
): TournamentState {
  if (matchIndex < 0 || matchIndex >= state.matches.length) {
    throw new Error(`Invalid matchIndex: ${matchIndex}`);
  }

  // Already recorded
  if (state.matches[matchIndex].result) {
    return state;
  }

  const newMatches = state.matches.map((m, i) =>
    i === matchIndex ? { ...m, result } : m
  );

  const newParticipants = state.participants.map((p) => {
    if (result.winnerId === p.id) {
      return { ...p, wins: p.wins + 1, points: p.points + 3 };
    }
    if (result.loserId === p.id) {
      return { ...p, losses: p.losses + 1 };
    }
    // Draw: both participants of this match get +1 draw, +1 point
    const match = state.matches[matchIndex];
    if (result.winnerId === null && (match.participant1Id === p.id || match.participant2Id === p.id)) {
      return { ...p, draws: p.draws + 1, points: p.points + 1 };
    }
    return p;
  });

  const allCompleted = newMatches.every((m) => m.result !== undefined);
  const phase = allCompleted ? TournamentPhase.FINISHED : TournamentPhase.IN_PROGRESS;

  return {
    ...state,
    matches: newMatches,
    participants: newParticipants,
    phase,
  };
}

/**
 * 次に実行すべき試合を返す。
 * 全試合完了の場合はnullを返す。
 * シングルエリミネーションでは、前ラウンドが未完了の場合そのラウンドの試合を返す。
 */
export function getNextMatch(state: TournamentState): TournamentMatch | null {
  if (state.mode === TournamentMode.SINGLE_ELIMINATION) {
    // ラウンド順に未完了の試合を探す
    const maxRound = Math.max(...state.matches.map((m) => m.round ?? 0));
    for (let round = 0; round <= maxRound; round++) {
      const roundMatches = state.matches.filter((m) => m.round === round);
      const incomplete = roundMatches.find((m) => !m.result && m.participant1Id !== '' && m.participant2Id !== '');
      if (incomplete) return incomplete;
    }
    return null;
  }

  // Round-robin: 単純に最初の未完了試合
  return state.matches.find((m) => !m.result) ?? null;
}

/**
 * プレイヤーが参加する試合かどうかを判定する。
 */
export function isPlayerMatch(_state: TournamentState, match: TournamentMatch): boolean {
  return match.participant1Id === 'player' || match.participant2Id === 'player';
}

/**
 * シングルエリミネーションで勝者を次ラウンドの試合に配置する。
 * 総当たりモードでは何もしない。
 *
 * ブラケット構造:
 * - QF0 (match 0) の勝者 → SF0 (match 4) の participant1
 * - QF1 (match 1) の勝者 → SF0 (match 4) の participant2
 * - QF2 (match 2) の勝者 → SF1 (match 5) の participant1
 * - QF3 (match 3) の勝者 → SF1 (match 5) の participant2
 * - SF0 (match 4) の勝者 → Final (match 6) の participant1
 * - SF1 (match 5) の勝者 → Final (match 6) の participant2
 */
export function advanceEliminationBracket(
  state: TournamentState,
  matchIndex: number,
): TournamentState {
  if (state.mode !== TournamentMode.SINGLE_ELIMINATION) {
    return state;
  }

  const match = state.matches[matchIndex];
  if (!match.result?.winnerId) return state;

  const winnerId = match.result.winnerId;
  const round = match.round ?? 0;

  // Map match index to next round slot
  let nextMatchIndex: number;
  let slot: 'participant1Id' | 'participant2Id';

  if (round === 0) {
    // QF → SF
    nextMatchIndex = 4 + Math.floor(matchIndex / 2);
    slot = matchIndex % 2 === 0 ? 'participant1Id' : 'participant2Id';
  } else if (round === 1) {
    // SF → Final
    nextMatchIndex = 6;
    slot = (matchIndex - 4) % 2 === 0 ? 'participant1Id' : 'participant2Id';
  } else {
    // Final has no next round
    return state;
  }

  const newMatches = state.matches.map((m, i) =>
    i === nextMatchIndex ? { ...m, [slot]: winnerId } : m
  );

  return { ...state, matches: newMatches };
}
